import React, { FC, useEffect } from 'react';
import { Redirect, Switch, useHistory } from 'react-router-dom';
import { RouteWrapper } from '../components/RouteWrapper';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { NotFoundComponent } from '../components/NotFound';
import { authenticatedRoot, AuthenticatedRoutes } from './AuthenticatedRoutes';
import { OrganizationUsersPage } from '../pages/CurrentOrganizationUsers';
import { CurrentOrganizationDetailsPage } from '../pages/CurrentOrganizationDetails';
import { OrganizationUsersFetcher } from '../fetchers/OrganizationUsersFetcher';
import { AdminProducts } from '../components/AdminProducts/AdminProducts';
import { Releases } from '../components/Releases/Releases';
import { ProductsFetcher } from '../fetchers/ProductsFetcher';
import { OrganizationsPage } from '../pages/Organizations';
import { OrganizationPage } from '../pages/Organization';
import { AllUsers } from '../components/AllUsers/AllUsers';
import { UserPage } from '../pages/User';
import { UserOrganizationPage } from '../pages/UserOrganization';
import { AdminsPage } from '../pages/Admins';
import { UserRolesPage } from '../pages/UserRoles';
import { UserDownloadsPage } from '../pages/UserDownloads';
import { DownloadsProductContentPage, DownloadsProductExamplesPage, DownloadsProductLensPage, DownloadsProductToolsPage } from '../components/DownloadsProductAddons';
import { DownloadsProductInstallerPage } from '../components/DownloadProductInstallerPage/DownloadsProductInstallerPage';
import { CurrentOrganizationProductsPage } from '../pages/CurrentOrganizationProducts';
import { OrganizationProduct } from '../components/CustomerProduct/OrganizationProduct';
import { DealHistory } from '../components/Customer/DealHistory';
import { DealHistoryFetcher } from '../fetchers/DealHistoryFetcher';
import { CurrentOrganizationInvitesPage } from '../pages/CurrentOrganizationInvites';
import { LicensesPage } from '../pages/Licenses';
import { AllLicensesPage } from '../pages/AllLicenses';
import { OrganizationContextProviderFromFetcher } from '../contexts/OrganizationContextProvider';
import { OrganizationFetcher } from '../components/Customer/CustomerFetchers';
import { AllLiveStacks } from '../components/AllLiveStacks/AllLiveStacks';
import { CloudLive } from '../components/CloudLive/CloudLive';
import { PendingTasks } from '../components/PendingTasks/PendingTasks';
import { ApiEvents } from '../components/ApiEvents/ApiEvents';
import { RedirectWithQuery } from './RedirectWithQuery';
import { useQueryParam } from '../hooks/useQueryParam';
import { UserDownloadsFetcher } from '../fetchers/UserDownloadsFetcher';
import { UnverifiedAccountPage } from '../pages/UnverifiedAccount';
import { AuthLayout } from '../layouts/AuthLayout';
import { CurrentOrganizationOverviewPage } from '../pages/CurrentOrganizationOverview';
import { GlobalOverviewPage } from '../pages/GlobalOverview';
import { PermissionsPage } from '../pages/Permissions';
import { Documentation } from '../pages/Documentation';
import { AnalyticsActivity } from '../components/AnalyticsActivity/AnalyticsActivity';
import { AnalyticsActivityAccount } from '../components/AnalyticsActivity/AnalyticsActivityAccount';
import { MainPage } from '../pages/Main';
import { useZendeskRedirect } from '../hooks/useZendeskRedirect';
import { ViewAccountPage } from '../pages/ViewAccount';
import { SupportPage } from '../pages/Support';

/* eslint prettier/prettier: ["error", { printWidth: 1000 }] */
const ProvidersProductsFetcher = [ProductsFetcher.WMF];

const ProvidersOrganization = [OrganizationFetcher.WMF, OrganizationContextProviderFromFetcher];

const useRedirect = () => {
  const redirect = useQueryParam('redirect');
  const history = useHistory();

  useEffect(() => {
    if (redirect) {
      history.replace(redirect);
    }
  }, [redirect]);
};

export const AuthenticatedRouter: FC = () => {
  const return_to = useQueryParam('return_to');

  const performZendeskRedirect = useZendeskRedirect();
  useRedirect();

  useEffect(() => {
    if (return_to) {
      performZendeskRedirect(return_to).then();
    }
  }, [return_to]);

  return (
    <Switch>
      <RedirectWithQuery exact from={AuthenticatedRoutes.ChooseOrganization()} to={authenticatedRoot} />

      <RouteWrapper exact path={'/panel/permissions'} component={PermissionsPage} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={authenticatedRoot} component={MainPage} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.Unverified()} component={UnverifiedAccountPage} layout={AuthLayout} />

      <RouteWrapper exact path={AuthenticatedRoutes.ViewAccount()} component={ViewAccountPage} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationOverview()} component={CurrentOrganizationOverviewPage} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationOverviewProduct()} component={CurrentOrganizationOverviewPage} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationUsers()} component={OrganizationUsersPage} layout={DashboardLayout} requireUserInfo={true} providers={[OrganizationUsersFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationDetails()} component={CurrentOrganizationDetailsPage} layout={DashboardLayout} requireUserInfo={true} providers={[OrganizationUsersFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationProducts()} component={CurrentOrganizationProductsPage} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationProduct()} component={OrganizationProduct} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationDealHistory()} component={DealHistory} layout={DashboardLayout} requireUserInfo={true} providers={[DealHistoryFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationInvites()} component={CurrentOrganizationInvitesPage} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.CurrentOrganizationCloudLive()} component={CloudLive} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.Licenses()} component={LicensesPage} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.AllLicenses()} component={AllLicensesPage} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.Support()} component={SupportPage} layout={DashboardLayout} requireUserInfo={true} />

      <Redirect exact from={'/'} to={authenticatedRoot} />
      {/*<Redirect from={guestRoot} to={authenticatedRoot} />*/}

      <RouteWrapper exact path={AuthenticatedRoutes.GlobalOverview()} component={GlobalOverviewPage} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.GlobalOverviewProduct()} component={GlobalOverviewPage} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.AdminProducts()} component={AdminProducts} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.AdminProductsTab()} component={AdminProducts} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.Releases()} component={Releases} providers={ProvidersProductsFetcher} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.ReleasesProduct()} component={Releases} providers={ProvidersProductsFetcher} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.ReleasesProductVersion()} component={Releases} providers={ProvidersProductsFetcher} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.Organizations()} component={OrganizationsPage} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.OrganizationDetails()} component={OrganizationPage} layout={DashboardLayout} requireUserInfo={true} providers={ProvidersOrganization} />

      <RouteWrapper exact path={AuthenticatedRoutes.OrganizationDetailsProducts()} component={OrganizationPage} layout={DashboardLayout} requireUserInfo={true} providers={ProvidersOrganization} />
      <RouteWrapper exact path={AuthenticatedRoutes.OrganizationDetailsUsers()} component={OrganizationPage} layout={DashboardLayout} requireUserInfo={true} providers={ProvidersOrganization} />
      <RouteWrapper exact path={AuthenticatedRoutes.OrganizationDetailsInvites()} component={OrganizationPage} layout={DashboardLayout} requireUserInfo={true} providers={ProvidersOrganization} />
      <RouteWrapper exact path={AuthenticatedRoutes.OrganizationDetailsTickets()} component={OrganizationPage} layout={DashboardLayout} requireUserInfo={true} providers={ProvidersOrganization} />
      <RouteWrapper exact path={AuthenticatedRoutes.OrganizationDetailsLive()} component={OrganizationPage} layout={DashboardLayout} requireUserInfo={true} providers={ProvidersOrganization} />
      <RouteWrapper exact path={AuthenticatedRoutes.OrganizationProduct()} component={OrganizationProduct} layout={DashboardLayout} requireUserInfo={true} providers={ProvidersOrganization} />

      <RouteWrapper exact path={AuthenticatedRoutes.AllUsers()} component={AllUsers} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.User()} component={UserPage} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.UserRoles()} component={UserRolesPage} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.UserOrganization()} component={UserOrganizationPage} layout={DashboardLayout} requireUserInfo={true} providers={ProvidersOrganization} />

      <RouteWrapper exact path={AuthenticatedRoutes.Admins()} component={AdminsPage} layout={DashboardLayout} requireUserInfo={true} />

      {/* Downloads */}
      <RouteWrapper exact path={AuthenticatedRoutes.UserDownloads()} component={UserDownloadsPage} layout={DashboardLayout} requireUserInfo={true} providers={[UserDownloadsFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.UserDownloadsProduct()} component={UserDownloadsPage} layout={DashboardLayout} requireUserInfo={true} providers={[UserDownloadsFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.UserDownloadsProductLens()} component={DownloadsProductLensPage} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.UserDownloadsProductVersion()} component={UserDownloadsPage} layout={DashboardLayout} requireUserInfo={true} providers={[UserDownloadsFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.UserDownloadsProductExamples()} component={DownloadsProductExamplesPage} layout={DashboardLayout} requireUserInfo={true} providers={[UserDownloadsFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.UserDownloadsProductContent()} component={DownloadsProductContentPage} layout={DashboardLayout} requireUserInfo={true} providers={[UserDownloadsFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.UserDownloadsProductTools()} component={DownloadsProductToolsPage} layout={DashboardLayout} requireUserInfo={true} providers={[UserDownloadsFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.UserDownloadsProductInstaller()} component={DownloadsProductInstallerPage} layout={DashboardLayout} requireUserInfo={true} providers={[UserDownloadsFetcher.WAF]} />
      <RouteWrapper exact path={AuthenticatedRoutes.Documentation()} component={Documentation} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.DocumentationProduct()} component={Documentation} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.CloudLive()} component={AllLiveStacks} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.Jobs()} component={PendingTasks} layout={DashboardLayout} requireUserInfo={true} />
      <RouteWrapper exact path={AuthenticatedRoutes.JobsType()} component={PendingTasks} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.Events()} component={ApiEvents} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.Activity()} component={AnalyticsActivity} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper exact path={AuthenticatedRoutes.ActivityItem()} component={AnalyticsActivityAccount} layout={DashboardLayout} requireUserInfo={true} />

      <RouteWrapper path={authenticatedRoot} component={NotFoundComponent} layout={DashboardLayout} requireUserInfo={true} />
    </Switch>
  );
};
